import * as React from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'

import Grid from '@mui/material/Grid'

import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'

import { renderImportOptionsMenu } from './SubImportMenuItem'

import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import StepButton from '@mui/material/StepButton'

import Typography from '@mui/material/Typography'

import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'
import EditableSelect from '../EditableSelect'
import TextField from '@mui/material/TextField'

import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import { UserContext } from '../../UserContext'

import { useTheme } from '@mui/material/styles'

import { SubImportContext } from './SubImportContext'
import { DialogsContext } from '../../DialogsContext'

import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import FormControlLabel from '@mui/material/FormControlLabel'

import Switch from '@mui/material/Switch'

import axios from 'axios'
import qs from 'qs'

import { slugifyString } from '../../utils/index'
import { generateTextImportItems } from './TextImportItems'
import ImportingNotice from './ImportingNotice'
import { importErrorMessage } from './importError'


const steps = ['Start', 'Sources', 'Settings', 'Results']

export default function SEOImportDialog({
    presetContext = '',
    presetSearchString = '',
    open,
    onClose,
}) {
    const theme = useTheme() // hook to get the current theme

    const {
        activeSubImportDialog,
        setActiveSubImportDialog,
    } = React.useContext(SubImportContext)

    const {
        activeImportDialog,
        setActiveImportDialog,
        backLinkDialog,
        openImportDialog,
        closeImportDialog,
        setPresetSearchString,
    } = React.useContext(DialogsContext)

    // Shared across all import sources so the typed query persists when switching
    const searchString = presetSearchString
    const setSearchString = setPresetSearchString

    const [selectedContext, setSelectedContext] = React.useState(presetContext)

    const [errorMessage, setErrorMessage] = React.useState(null)

    const [importError, setImportError] = React.useState(null)

    const [completedSteps, setCompletedSteps] = React.useState([])

    const [visitedContext, setVisitedContext] = React.useState(false)

    const [searchLanguage, setSearchLanguage] = React.useState('Default')

    const [searchCountry, setSearchCountry] = React.useState('US')

    const [hideSearchTerms, setHideSearchTerms] = React.useState(true)

    const [dataSource, setDataSource] = React.useState('related')

    const [analyzeKeywordsOnly, setAnalyzeKeywordsOnly] = React.useState(false)

    const [advancedSettings, setAdvancedSettings] = React.useState(false)

    const [modifyAnalyzedText, setModifyAnalyzedText] = React.useState('none')

    const menuItems = generateTextImportItems()

    const filteredMenuItems = menuItems.filter(
        ({ textInput }) =>
            textInput.includes('seo') || textInput.includes(backLinkDialog)
    )

    const { username } = React.useContext(UserContext)
    const contexts = useContextsStore(selectOwnedContexts)
    const contextNames = contexts.map(c => c.name)

    const [activeStep, setActiveStep] = React.useState(2)
    const [skipped, setSkipped] = React.useState(new Set())

    React.useEffect(() => {
        const savedAdvancedImport = localStorage.getItem('import_advanced')

        if (savedAdvancedImport === '1') {
            setAdvancedSettings(true)
        }

        const savedLanguage = localStorage.getItem('import_language')

        if (savedLanguage) {
            setSearchLanguage(savedLanguage)
        }

        const savedCountry = localStorage.getItem('import_country')

        if (savedCountry) {
            setSearchCountry(savedCountry)
        }
    }, [])

    React.useEffect(() => {
        localStorage.setItem('import_advanced', advancedSettings ? '1' : '0')
    }, [advancedSettings])

    React.useEffect(() => {
        localStorage.setItem('import_country', searchCountry)
    }, [searchCountry])

    React.useEffect(() => {
        localStorage.setItem('import_language', searchLanguage)
    }, [searchLanguage])

    const handleNext = () => {
        let newSkipped = skipped

        setActiveStep(prevActiveStep => prevActiveStep + 1)
        setSkipped(newSkipped)
    }

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1)
    }

    const handleStep = step => {
        setActiveStep(step)
    }

    const handleSearchStringChange = event => {
        setSearchString(event.target.value)
    }

    const handleSearchLanguageChange = event => {
        setSearchLanguage(event.target.value)
    }

    const handleSearchCountryChange = event => {
        setSearchCountry(event.target.value)
    }

    const handleDataSourceChange = event => {
        setDataSource(event.target.value)
    }

    React.useEffect(() => {
        if (activeSubImportDialog.type) {
            setActiveStep(2)
            setActiveImportDialog(activeSubImportDialog.type)
        }
    }, [activeSubImportDialog])

    React.useEffect(() => {
        if (
            !presetContext &&
            selectedContext &&
            selectedContext.indexOf('seo_') !== 0
        ) {
            setSelectedContext(`seo_${selectedContext}`)
            setErrorMessage(
                `We automatically corrected the name to start with "seo_", as it's a requirement for this type of data import`
            )
        }
    }, [selectedContext])

    React.useEffect(() => {
        switch (activeStep) {
            case 0: // Start
                setActiveSubImportDialog(false)
                setActiveImportDialog('import')
                break
            case 1: // Sources
                setActiveSubImportDialog(false)
                setActiveImportDialog('shortTextImport')
                break
            case 2: // Settings
                if (!presetContext) {
                    setSelectedContext(generatePresetContext(searchString))
                }
                break
            case 3: // Analysis
                if (!searchString) {
                    setErrorMessage(
                        `Please, provide a search query you'd like to get results from Google for.`
                    )
                    setActiveStep(2)
                    break
                }
                if (!selectedContext) {
                    setErrorMessage(
                        'Please, select the graph to import your data to.'
                    )
                    setActiveStep(2)
                    break
                }
                if (selectedContext) {
                    setCompletedSteps([...completedSteps, 3])
                    setErrorMessage(null)
                    submitDataForm()
                    break
                }
        }
    }, [activeStep])

    const submitDataForm = () => {
        const dataToSend = {
            searchQuery: searchString,
            context: selectedContext,
            origin: 'googlesearchseo',
            source: 'googlesearchseo',
            excludesearchquery: hideSearchTerms ? '1' : '',
            limit: '40',
            importLanguage: searchLanguage === 'Default' ? '' : searchLanguage,
            importCountry: searchCountry,
            settings: 'morphemes',
            serp_origin_first_niche: 'suggestedkeywords',
            serp_source_first_niche: 'suggestedkeywords',
            sourceapp: 'seo',
            search_queries: dataSource,
            full_seo: analyzeKeywordsOnly ? '1' : '', // 1 — only keyword suggestions,
            modifyAnalyzedText: modifyAnalyzedText,
        }

        const dataAsForm = qs.stringify(dataToSend)

        axios
            .post('/import/seo', dataAsForm, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded', // Sending as URL-encoded form data
                },
            })
            .then(response => {
                if (response.data.redirectUrl) {
                    // If you're using window.location
                    window.location.href = response.data.redirectUrl

                    // Or, if you're using React Router's history (assuming you have access to the history object)
                    // history.push(response.data.redirectUrl);
                } else {
                    console.error(`Did't receive any response from the server.`)
                    if (response.data.error) {
                        setErrorMessage(
                            typeof response.data.error == 'object'
                                ? response.data.error.error ||
                                      JSON.stringify(response.data.error)
                                : response.data.error
                        )
                        setImportError(response.data.error)
                        setActiveStep(2)
                    }
                }
            })
            .catch(error => {
                // Handle the error here
                console.error('Received the following error', error)
                setErrorMessage(importErrorMessage(error))
                setImportError(response.data.error)
            })
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogTitle
                    sx={{
                        padding: 4,
                        color: theme => theme.palette.text.primary,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {'Compare Search Queries to Search Results'}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Stepper
                    activeStep={activeStep}
                    sx={{ paddingLeft: 4, paddingRight: 4, paddingTop: 2 }}
                >
                    {steps.map((label, index) => {
                        const stepProps = {}
                        const labelProps = {}

                        return (
                            <Step key={label} {...stepProps}>
                                <StepButton
                                    onClick={() => handleStep(index)}
                                    completed={(
                                        completedSteps.indexOf(index) >= 0
                                    ).toString()}
                                >
                                    <StepLabel
                                        {...labelProps}
                                        sx={{
                                            '& .MuiStepLabel-label': {
                                                fontSize: {
                                                    xs: '0.75rem',
                                                    sm: 'initial',
                                                },
                                            },
                                        }}
                                    >
                                        {label}
                                    </StepLabel>
                                </StepButton>
                            </Step>
                        )
                    })}
                </Stepper>

                {errorMessage && (
                    <Typography
                        sx={{
                            paddingRight: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                            paddingBottom: 0,
                        }}
                        color="error"
                        component="div"
                        gutterBottom
                    >
                        {errorMessage}
                    </Typography>
                )}

                {activeStep === 2 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            <Grid container spacing={8}>
                                <Grid item xs={12} sm={7}>
                                    <DialogContentText>
                                        Please, enter a search query to discover
                                        what else people are searching for
                                    </DialogContentText>
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 4 }}
                                    >
                                        <TextField
                                            label={
                                                'Search query (or comma-separated for multiple queries)'
                                            }
                                            value={searchString}
                                            variant="outlined"
                                            onChange={handleSearchStringChange}
                                            aria-describedby="searchString-helperText"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.main',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.dark',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderWidth: 2,
                                                    },
                                                },
                                            }}
                                        />

                                        {searchString &&
                                            !searchString.includes(',') &&
                                            (searchString.trim().split(/\s+/)
                                                .length > 3 ||
                                                searchString.trim().length >
                                                    30) && (
                                                <Typography
                                                    variant="caption"
                                                    color="warning.main"
                                                    sx={{ mt: 1 }}
                                                >
                                                    We recommend to use shorter,
                                                    more general queries for
                                                    this SEO app.
                                                </Typography>
                                            )}

                                        <FormHelperText
                                            id="searchString-helperText"
                                            sx={{ marginTop: 2 }}
                                        >
                                            we will present the two graphs: what
                                            people search for and what they
                                            find, and highlight the missing
                                            keyword combinations for all the
                                            queries (max 5)
                                        </FormHelperText>
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 6 }}
                                    >
                                        <DialogContentText>
                                            Using the import app:
                                        </DialogContentText>
                                        <DialogContent
                                            sx={{
                                                pl: 0,
                                                ml: 0,
                                                marginTop: -2,
                                            }}
                                        >
                                            {renderImportOptionsMenu(
                                                filteredMenuItems,
                                                'seo'
                                            )}
                                        </DialogContent>
                                    </FormControl>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                    sx={{
                                        marginTop: 3,
                                        marginBottom: 4,
                                    }}
                                >
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 4, marginBottom: 2 }}
                                    >
                                        <EditableSelect
                                            selectItems={contextNames}
                                            label={'Import to Graph'}
                                            initialValue={selectedContext}
                                            onChange={name =>
                                                setSelectedContext(name)
                                            }
                                        />
                                    </FormControl>
                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 4,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <InputLabel id="dataSource-label">
                                            Source of Data
                                        </InputLabel>
                                        <Select
                                            labelId="dataSource-label"
                                            id="dataSource"
                                            value={dataSource}
                                            onChange={handleDataSourceChange}
                                            label="Source of Data"
                                        >
                                            <MenuItem value={'related'}>
                                                Search Results vs Related Search
                                                Queries
                                            </MenuItem>
                                            <MenuItem value={'adwords'}>
                                                Search Results vs AdWords
                                                Suggestions
                                            </MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 4,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={advancedSettings}
                                                    onChange={event =>
                                                        setAdvancedSettings(
                                                            event.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label="Show advanced settings"
                                        />
                                    </FormControl>

                                    {advancedSettings && (
                                        <>
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="searchLanguage-label">
                                                    Language of the Search
                                                    Results
                                                </InputLabel>
                                                <Select
                                                    labelId="searchLanguage-label"
                                                    id="searchLanguage"
                                                    value={searchLanguage}
                                                    onChange={
                                                        handleSearchLanguageChange
                                                    }
                                                    label="Language of the Search Results"
                                                >
                                                    <MenuItem value={'Default'}>
                                                        Default Language
                                                    </MenuItem>
                                                    <MenuItem value={'EN'}>
                                                        English
                                                    </MenuItem>
                                                    <MenuItem value={'CN'}>
                                                        Chinese (Simplified)
                                                    </MenuItem>
                                                    <MenuItem value={'TW'}>
                                                        Chinese (Traditional)
                                                    </MenuItem>
                                                    <MenuItem value={'NL'}>
                                                        Dutch
                                                    </MenuItem>
                                                    <MenuItem value={'FR'}>
                                                        French
                                                    </MenuItem>
                                                    <MenuItem value={'DE'}>
                                                        German
                                                    </MenuItem>
                                                    <MenuItem value={'IT'}>
                                                        Italian
                                                    </MenuItem>
                                                    <MenuItem value={'JP'}>
                                                        Japanese
                                                    </MenuItem>
                                                    <MenuItem value={'NO'}>
                                                        Norwegian
                                                    </MenuItem>
                                                    <MenuItem value={'PT'}>
                                                        Portugese
                                                    </MenuItem>
                                                    <MenuItem value={'RU'}>
                                                        Russian
                                                    </MenuItem>
                                                    <MenuItem value={'ES'}>
                                                        Spanish
                                                    </MenuItem>
                                                    <MenuItem value={'SV'}>
                                                        Swedish
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>

                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="searchCountry-label">
                                                    For Users Located In
                                                </InputLabel>
                                                <Select
                                                    labelId="searchCountry-label"
                                                    id="searchCountry"
                                                    value={searchCountry}
                                                    onChange={
                                                        handleSearchCountryChange
                                                    }
                                                    label="For Users Located In"
                                                >
                                                    <MenuItem value={'US'}>
                                                        USA
                                                    </MenuItem>
                                                    <MenuItem value={'AR'}>
                                                        Argentina
                                                    </MenuItem>
                                                    <MenuItem value={'AU'}>
                                                        Australia
                                                    </MenuItem>
                                                    <MenuItem value={'BR'}>
                                                        Brazil
                                                    </MenuItem>
                                                    <MenuItem value={'CA'}>
                                                        Canada
                                                    </MenuItem>
                                                    <MenuItem value={'CN'}>
                                                        China
                                                    </MenuItem>
                                                    <MenuItem value={'FR'}>
                                                        France
                                                    </MenuItem>
                                                    <MenuItem value={'DE'}>
                                                        Germany
                                                    </MenuItem>
                                                    <MenuItem value={'GB'}>
                                                        Great Britain
                                                    </MenuItem>
                                                    <MenuItem value={'HK'}>
                                                        Hong-Kong
                                                    </MenuItem>
                                                    <MenuItem value={'IN'}>
                                                        India
                                                    </MenuItem>
                                                    <MenuItem value={'IT'}>
                                                        Italy
                                                    </MenuItem>
                                                    <MenuItem value={'JP'}>
                                                        Japan
                                                    </MenuItem>
                                                    <MenuItem value={'MX'}>
                                                        Mexico
                                                    </MenuItem>
                                                    <MenuItem value={'NL'}>
                                                        Netherlands
                                                    </MenuItem>
                                                    <MenuItem value={'NZ'}>
                                                        New Zealand
                                                    </MenuItem>
                                                    <MenuItem value={'NO'}>
                                                        Norway
                                                    </MenuItem>
                                                    <MenuItem value={'PT'}>
                                                        Portugal
                                                    </MenuItem>
                                                    <MenuItem value={'RU'}>
                                                        Russia
                                                    </MenuItem>
                                                    <MenuItem value={'ES'}>
                                                        Spain
                                                    </MenuItem>
                                                    <MenuItem value={'SV'}>
                                                        Sweden
                                                    </MenuItem>
                                                    <MenuItem value={'TW'}>
                                                        Taiwan
                                                    </MenuItem>
                                                    <MenuItem value={'CH'}>
                                                        Switzerland
                                                    </MenuItem>
                                                    <MenuItem value={'GB'}>
                                                        United Kingdom
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={
                                                                analyzeKeywordsOnly
                                                            }
                                                            onChange={event =>
                                                                setAnalyzeKeywordsOnly(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Only Keyword Suggestions, No Search Results"
                                                />

                                                <FormHelperText>
                                                    do not include the Google
                                                    search results comparison
                                                    graph
                                                </FormHelperText>
                                            </FormControl>
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="modifyAnalyzedText-label">
                                                    Build a knowledge graph from
                                                </InputLabel>
                                                <Select
                                                    labelId="modifyAnalyzedText-label"
                                                    id="modifyAnalyzedText"
                                                    value={modifyAnalyzedText}
                                                    onChange={event =>
                                                        setModifyAnalyzedText(
                                                            event.target.value
                                                        )
                                                    }
                                                    label="Build a knowledge graph from"
                                                >
                                                    <MenuItem
                                                        value={'none'}
                                                        key="1"
                                                    >
                                                        the words used in text
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={'detectEntities'}
                                                        key="2"
                                                    >
                                                        the words and detected
                                                        entities
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={
                                                            'extractEntitiesOnly'
                                                        }
                                                        key="3"
                                                    >
                                                        only the detected
                                                        entities
                                                    </MenuItem>
                                                </Select>
                                                {modifyAnalyzedText !=
                                                    'none' && (
                                                    <FormHelperText
                                                        sx={{ marginTop: 1 }}
                                                    >
                                                        By default, we extract
                                                        the words used in your
                                                        text. You can also
                                                        choose to detect and tag
                                                        entities in your text
                                                        and build a graph from
                                                        them.
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={
                                                                hideSearchTerms
                                                            }
                                                            onChange={event =>
                                                                setHideSearchTerms(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Hide Search Terms from Graph"
                                                />

                                                <FormHelperText>
                                                    explore the context around
                                                    the search query
                                                </FormHelperText>
                                            </FormControl>
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}

                {activeStep === 3 && (
                    <ImportingNotice
                        linkHref={`/${username}/${selectedContext}/seo`}
                    />
                )}
                <DialogActions
                    sx={{
                        paddingRight: 4,
                        paddingLeft: 4,
                        paddingBottom: 4,
                        paddingTop: 2,
                    }}
                >
                    {/* <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button> */}
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                        Back
                    </Button>

                    <LoadingButton
                        color="info"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        disabled={!searchString.length > 0 || activeStep == 3}
                        onClick={handleNext}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        {activeStep === 2 ? 'Visualize' : 'Next'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

function generatePresetContext(searchString) {
    const dateAndTime = new Date()
        .toISOString()
        .slice(2, 16)
        .replace(/-/g, '')
        .replace(/:/g, '')

    if (!searchString) return `seo_${dateAndTime}`

    const twoFirstSearchQueryWords = slugifyString(
        searchString
            .split(' ')
            .slice(0, 2)
            .join('_')
            .slice(0, 20)
            .replace(/[^\P{L}a-z][^a-z]*/giu, '')
    )

    return `seo_${twoFirstSearchQueryWords}`
}
